import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  X, Copy, CheckCircle2, Clock, AlertCircle, Loader2,
  Wallet, ArrowRight, Shield, QrCode, ChevronDown, Info
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import PaymentService, { PAYMENT_TYPES } from '../../services/paymentService';
import { useLocale } from '../../contexts/LocaleContext';

const NETWORK_CONFIG = {
  ethereum: { bg: '#627EEA', label: 'ETH', name: 'Ethereum', icon: '⟠' },
  bitcoin:  { bg: '#F7931A', label: 'BTC', name: 'Bitcoin',  icon: '₿' },
  bsc:      { bg: '#F3BA2F', label: 'BSC', name: 'BNB Chain', icon: '◈' },
  tron:     { bg: '#FF0013', label: 'TRX', name: 'Tron',     icon: '◉' },
  polygon:  { bg: '#8247E5', label: 'POL', name: 'Polygon',  icon: '⬡' },
};

// Explicit token descriptions so users know exactly what to send
const TOKEN_DESCRIPTIONS = {
  ETH:  'Ether — native Ethereum coin',
  BTC:  'Bitcoin — native Bitcoin coin',
  BNB:  'BNB — native BNB Chain coin',
  TRX:  'Tron — native Tron coin',
  MATIC:'Polygon — native Polygon coin',
  USDT: 'Tether USD — ERC-20/BEP-20 stablecoin',
  USDC: 'USD Coin — ERC-20 stablecoin',
  BUSD: 'Binance USD — BEP-20 stablecoin',
  DAI:  'Dai — ERC-20 stablecoin',
};

function NetworkBadge({ network, size = 36 }) {
  const info = NETWORK_CONFIG[network] || { bg: '#888', label: '?', icon: '?' };
  return (
    <div style={{
      width: size, height: size, borderRadius: 10,
      backgroundColor: info.bg,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontWeight: 700, color: 'white', fontSize: size * 0.35,
      flexShrink: 0,
    }}>
      {info.label}
    </div>
  );
}

function CopyBtn({ text, label = 'Copy' }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button onClick={copy} style={{
      display: 'flex', alignItems: 'center', gap: 4,
      padding: '4px 10px', borderRadius: 8, fontSize: 11, fontWeight: 600,
      cursor: 'pointer', border: 'none',
      backgroundColor: copied ? 'color-mix(in srgb, var(--accent-success) 15%, transparent)' : 'var(--bg-tertiary)',
      color: copied ? 'var(--accent-success)' : 'var(--text-secondary)',
      transition: 'all 0.15s',
    }}>
      {copied ? <CheckCircle2 size={12} /> : <Copy size={12} />}
      {copied ? 'Copied!' : label}
    </button>
  );
}

function Countdown({ expiresAt }) {
  const [remaining, setRemaining] = useState('');
  useEffect(() => {
    const update = () => {
      const diff = new Date(expiresAt) - Date.now();
      if (diff <= 0) { setRemaining('Expired'); return; }
      const m = Math.floor(diff / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setRemaining(`${m}:${s.toString().padStart(2, '0')}`);
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [expiresAt]);
  const isLow = remaining && !remaining.includes('Expired') && parseInt(remaining) < 5;
  return (
    <span style={{ fontFamily: 'monospace', fontSize: 14, fontWeight: 700,
      color: isLow ? 'var(--accent-danger)' : 'var(--text-primary)' }}>
      {remaining}
    </span>
  );
}

export default function CryptoCheckout({ type = PAYMENT_TYPES.PLUGIN_PURCHASE, item, plugin, onClose, onSuccess }) {
  const { t } = useLocale();
  const [step, setStep] = useState('select');
  const [networks, setNetworks] = useState([]);
  const [selectedNetwork, setSelectedNetwork] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showQR, setShowQR] = useState(false);
  const pollRef = useRef(null);
  const actualItem = item || plugin;

  const getItemDetails = () => {
    if (!actualItem) return { title: 'Make Payment', price: 0, cycle: 'one-time' };
    switch (type) {
      case PAYMENT_TYPES.PLUGIN_PURCHASE:
        return { title: `Purchase ${actualItem.name || actualItem.label}`, price: parseFloat(actualItem.price || 0), cycle: actualItem.billing_cycle === 'one_time' ? 'one-time' : actualItem.billing_cycle || 'one-time' };
      case PAYMENT_TYPES.DEPOSIT:
        return { title: 'Deposit Funds', price: actualItem.amount || 0, cycle: 'one-time' };
      case PAYMENT_TYPES.SUBSCRIPTION:
        return { title: `Subscribe to ${actualItem.name}`, price: actualItem.price || 0, cycle: actualItem.billing_cycle || 'monthly' };
      default:
        return { title: 'Make Payment', price: actualItem.amount || actualItem.price || 0, cycle: 'one-time' };
    }
  };
  const itemDetails = getItemDetails();

  useEffect(() => {
    PaymentService.getPaymentOptions('amorpay')
      .then(data => setNetworks(data || []))
      .catch(() => setError('Failed to load payment options'));
  }, []);

  const startPolling = useCallback((paymentId) => {
    if (pollRef.current) pollRef.current.stop();
    pollRef.current = PaymentService.pollPaymentStatus(paymentId, type, (status) => {
      if (status.confirmed || status.status === 'confirmed') {
        setPayment(prev => ({ ...prev, ...status }));
        setStep('confirmed');
        if (pollRef.current) pollRef.current.stop();
      } else if (status.status === 'expired') {
        setError('Payment expired. Please try again.');
        setStep('select');
        if (pollRef.current) pollRef.current.stop();
      }
    }, { initialInterval: 5000, maxInterval: 15000, maxAttempts: 60 });
  }, [type]);

  useEffect(() => () => { if (pollRef.current) pollRef.current.stop(); }, []);

  const handleNetworkSelect = (net) => {
    setSelectedNetwork(net.network);
    // Don't auto-select currency — force explicit choice
    setSelectedCurrency(null);
  };

  const handlePurchase = async () => {
    if (!selectedNetwork || !selectedCurrency) return;
    if (!actualItem) { setError('No item selected'); return; }
    setLoading(true);
    setError(null);
    try {
      let paymentData;
      if (type === PAYMENT_TYPES.PLUGIN_PURCHASE) {
        paymentData = await PaymentService.createPluginPayment(
          actualItem.plugin_key || actualItem.pluginKey, selectedCurrency, selectedNetwork);
      } else if (type === PAYMENT_TYPES.DEPOSIT) {
        paymentData = await PaymentService.createDeposit(
          actualItem.account_id, actualItem.amount, selectedCurrency, selectedNetwork);
      } else if (type === PAYMENT_TYPES.SUBSCRIPTION) {
        paymentData = await PaymentService.createSubscription(
          actualItem.plan_id, selectedCurrency, selectedNetwork);
      } else {
        paymentData = await PaymentService.createPayment({
          type, currency: selectedCurrency, network: selectedNetwork,
          amount: actualItem.amount, metadata: actualItem,
        });
      }
      if (paymentData.free || paymentData.activated) { setStep('confirmed'); return; }
      setPayment(paymentData);
      setStep('awaiting');
      startPolling(paymentData.payment_id || paymentData.gateway_payment_id);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Payment creation failed');
    } finally {
      setLoading(false);
    }
  };

  const selectedNetworkData = networks.find(n => n.network === selectedNetwork);

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 100,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 16, backgroundColor: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(6px)',
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        width: '100%', maxWidth: 460, borderRadius: 20,
        border: '1px solid var(--border-color)',
        backgroundColor: 'var(--bg-primary)',
        overflow: 'hidden',
        boxShadow: '0 24px 64px rgba(0,0,0,0.3)',
      }}>

        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 20px', borderBottom: '1px solid var(--border-color)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10,
              backgroundColor: 'color-mix(in srgb, var(--accent-primary) 15%, transparent)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Wallet size={16} style={{ color: 'var(--accent-primary)' }} />
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>
                {step === 'confirmed' ? 'Payment Confirmed' : itemDetails.title}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
                ${itemDetails.price.toFixed(2)} · {itemDetails.cycle}
              </div>
            </div>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            padding: 6, borderRadius: 8, color: 'var(--text-tertiary)',
          }}>
            <X size={16} />
          </button>
        </div>

        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Error */}
          {error && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '10px 12px', borderRadius: 12,
              backgroundColor: 'color-mix(in srgb, var(--accent-danger) 8%, transparent)',
              border: '1px solid color-mix(in srgb, var(--accent-danger) 20%, transparent)',
              color: 'var(--accent-danger)', fontSize: 12,
            }}>
              <AlertCircle size={14} style={{ flexShrink: 0 }} />
              <span style={{ flex: 1 }}>{error}</span>
              <button onClick={() => setError(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit' }}>
                <X size={12} />
              </button>
            </div>
          )}

          {/* ── STEP 1: Select Network + Currency ── */}
          {step === 'select' && (
            <>
              {/* Network Selection */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-tertiary)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Step 1 — Choose Blockchain Network
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {networks.length === 0 && !error && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32 }}>
                      <Loader2 size={24} style={{ color: 'var(--accent-primary)', animation: 'spin 1s linear infinite' }} />
                    </div>
                  )}
                  {networks.map(net => (
                    <button key={net.network} onClick={() => handleNetworkSelect(net)} style={{
                      width: '100%', display: 'flex', alignItems: 'center', gap: 12,
                      padding: '10px 14px', borderRadius: 14, textAlign: 'left', cursor: 'pointer',
                      backgroundColor: selectedNetwork === net.network
                        ? 'color-mix(in srgb, var(--accent-primary) 8%, transparent)'
                        : 'var(--bg-secondary)',
                      border: `1.5px solid ${selectedNetwork === net.network ? 'var(--accent-primary)' : 'var(--border-color)'}`,
                      transition: 'all 0.15s',
                    }}>
                      <NetworkBadge network={net.network} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
                          {NETWORK_CONFIG[net.network]?.name || net.network_name || net.network}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>
                          Supports: {(net.supported_currencies || []).join(', ')}
                        </div>
                      </div>
                      {selectedNetwork === net.network && (
                        <CheckCircle2 size={18} style={{ color: 'var(--accent-primary)', flexShrink: 0 }} />
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Currency Selection — only shown after network picked */}
              {selectedNetwork && selectedNetworkData && (
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-tertiary)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Step 2 — Choose Token to Send
                  </div>

                  {/* Warning box */}
                  <div style={{
                    display: 'flex', gap: 8, padding: '10px 12px', borderRadius: 10, marginBottom: 10,
                    backgroundColor: 'color-mix(in srgb, var(--accent-warning) 8%, transparent)',
                    border: '1px solid color-mix(in srgb, var(--accent-warning) 20%, transparent)',
                  }}>
                    <Info size={14} style={{ color: 'var(--accent-warning)', flexShrink: 0, marginTop: 1 }} />
                    <p style={{ fontSize: 11, color: 'var(--accent-warning)', margin: 0, lineHeight: 1.5 }}>
                      You must send <strong>exactly the token you select here</strong>. Sending a different token (e.g. USDT instead of ETH) will result in a failed payment that cannot be automatically recovered.
                    </p>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {(selectedNetworkData.supported_currencies || []).map(currency => (
                      <button key={currency} onClick={() => setSelectedCurrency(currency)} style={{
                        width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                        padding: '10px 14px', borderRadius: 12, textAlign: 'left', cursor: 'pointer',
                        backgroundColor: selectedCurrency === currency
                          ? 'color-mix(in srgb, var(--accent-primary) 8%, transparent)'
                          : 'var(--bg-secondary)',
                        border: `1.5px solid ${selectedCurrency === currency ? 'var(--accent-primary)' : 'var(--border-color)'}`,
                        transition: 'all 0.15s',
                      }}>
                        <div style={{
                          width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                          backgroundColor: NETWORK_CONFIG[selectedNetwork]?.bg || '#888',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 10, fontWeight: 800, color: 'white',
                        }}>
                          {currency.slice(0, 4)}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>
                            {currency}
                          </div>
                          <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 1 }}>
                            {TOKEN_DESCRIPTIONS[currency] || currency}
                          </div>
                        </div>
                        {selectedCurrency === currency && (
                          <CheckCircle2 size={16} style={{ color: 'var(--accent-primary)', flexShrink: 0 }} />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Summary before confirming */}
              {selectedNetwork && selectedCurrency && (
                <div style={{
                  padding: '12px 14px', borderRadius: 12,
                  backgroundColor: 'color-mix(in srgb, var(--accent-primary) 6%, transparent)',
                  border: '1px solid color-mix(in srgb, var(--accent-primary) 15%, transparent)',
                }}>
                  <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 6 }}>Payment Summary</div>
                  <div style={{ fontSize: 12, color: 'var(--text-primary)', display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: 'var(--text-secondary)' }}>Network</span>
                      <strong>{NETWORK_CONFIG[selectedNetwork]?.name || selectedNetwork}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: 'var(--text-secondary)' }}>Token</span>
                      <strong>{selectedCurrency}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: 'var(--text-secondary)' }}>USD Value</span>
                      <strong>${itemDetails.price.toFixed(2)}</strong>
                    </div>
                  </div>
                </div>
              )}

              <button onClick={handlePurchase} disabled={!selectedNetwork || !selectedCurrency || loading} style={{
                width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                padding: '13px 0', borderRadius: 14, fontSize: 13, fontWeight: 700, color: 'white',
                backgroundColor: 'var(--accent-primary)', border: 'none', cursor: 'pointer',
                opacity: (!selectedNetwork || !selectedCurrency || loading) ? 0.4 : 1,
                transition: 'opacity 0.15s',
              }}>
                {loading ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <ArrowRight size={16} />}
                {loading ? 'Creating Payment...' : (!selectedCurrency ? 'Select a Token to Continue' : 'Generate Payment Address')}
              </button>
            </>
          )}

          {/* ── STEP 2: Awaiting Payment ── */}
          {step === 'awaiting' && payment && (
            <>
              {/* Big send instruction box */}
              <div style={{
                borderRadius: 16, overflow: 'hidden',
                border: '2px solid color-mix(in srgb, var(--accent-primary) 30%, transparent)',
              }}>
                {/* Token banner */}
                <div style={{
                  padding: '10px 16px',
                  backgroundColor: 'color-mix(in srgb, var(--accent-primary) 12%, transparent)',
                  display: 'flex', alignItems: 'center', gap: 8,
                }}>
                  <NetworkBadge network={payment.network} size={28} />
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent-primary)' }}>
                    Send on {NETWORK_CONFIG[payment.network]?.name || payment.network} Network
                  </div>
                </div>

                <div style={{ padding: '16px', backgroundColor: 'var(--bg-secondary)', textAlign: 'center' }}>
                  <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 6 }}>
                    Send exactly this amount
                  </div>
                  <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 8 }}>
                    <span style={{ fontSize: 28, fontWeight: 900, fontFamily: 'monospace', color: 'var(--text-primary)' }}>
                      {payment.amount}
                    </span>
                    <span style={{
                      fontSize: 18, fontWeight: 800, color: 'var(--accent-primary)',
                      padding: '2px 8px', borderRadius: 6,
                      backgroundColor: 'color-mix(in srgb, var(--accent-primary) 12%, transparent)',
                    }}>
                      {payment.currency}
                    </span>
                  </div>
                  {payment.fiatAmount && (
                    <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>
                      ≈ ${payment.fiatAmount.toFixed(2)} USD
                    </div>
                  )}

                  {/* Token clarification */}
                  <div style={{
                    marginTop: 12, padding: '8px 12px', borderRadius: 8,
                    backgroundColor: 'color-mix(in srgb, var(--accent-danger) 8%, transparent)',
                    border: '1px solid color-mix(in srgb, var(--accent-danger) 20%, transparent)',
                    fontSize: 11, color: 'var(--accent-danger)', textAlign: 'left',
                    display: 'flex', gap: 6, alignItems: 'flex-start',
                  }}>
                    <AlertCircle size={13} style={{ flexShrink: 0, marginTop: 1 }} />
                    <span>
                      Send <strong>{payment.currency}</strong> only — not USDT, not any other token.
                      {TOKEN_DESCRIPTIONS[payment.currency] && <> ({TOKEN_DESCRIPTIONS[payment.currency]})</>}
                    </span>
                  </div>
                </div>
              </div>

              {/* QR Code Toggle */}
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <button onClick={() => setShowQR(!showQR)} style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '8px 16px', borderRadius: 12,
                  backgroundColor: showQR ? 'color-mix(in srgb, var(--accent-primary) 10%, transparent)' : 'var(--bg-secondary)',
                  border: `1px solid ${showQR ? 'var(--accent-primary)' : 'var(--border-color)'}`,
                  color: showQR ? 'var(--accent-primary)' : 'var(--text-secondary)',
                  fontSize: 12, fontWeight: 600, cursor: 'pointer',
                }}>
                  <QrCode size={14} />
                  {showQR ? 'Hide QR Code' : 'Show QR Code'}
                </button>
              </div>

              {showQR && (
                <div style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
                  padding: 16, borderRadius: 16, border: '1px solid var(--border-color)',
                  backgroundColor: 'var(--bg-secondary)',
                }}>
                  <div style={{ padding: 12, borderRadius: 12, backgroundColor: 'white' }}>
                    <QRCodeSVG value={payment.payment_address || payment.walletAddress} size={180} level="H" includeMargin={false} />
                  </div>
                  <p style={{ fontSize: 10, color: 'var(--text-tertiary)', margin: 0 }}>
                    Scan with your crypto wallet
                  </p>
                </div>
              )}

              {/* Address */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-tertiary)' }}>
                    Send to this address ({NETWORK_CONFIG[payment.network]?.name || payment.network})
                  </span>
                  <CopyBtn text={payment.payment_address || payment.walletAddress} label="Copy Address" />
                </div>
                <div style={{
                  padding: '10px 12px', borderRadius: 12,
                  border: '1px solid var(--border-color)',
                  backgroundColor: 'var(--bg-secondary)',
                  fontFamily: 'monospace', fontSize: 11,
                  color: 'var(--text-primary)', wordBreak: 'break-all', lineHeight: 1.6,
                }}>
                  {payment.payment_address || payment.walletAddress}
                </div>
              </div>

              {/* Checklist */}
              <div style={{
                padding: '12px 14px', borderRadius: 12,
                backgroundColor: 'var(--bg-secondary)',
                border: '1px solid var(--border-color)',
              }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 8 }}>
                  Before you send — verify:
                </div>
                {[
                  `Token is ${payment.currency} (not USDT or any other coin)`,
                  `Network is ${NETWORK_CONFIG[payment.network]?.name || payment.network}`,
                  `Amount is exactly ${payment.amount} ${payment.currency}`,
                  'Address matches exactly (copy, don\'t type)',
                ].map((item, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: i < 3 ? 6 : 0 }}>
                    <div style={{
                      width: 16, height: 16, borderRadius: '50%', flexShrink: 0, marginTop: 1,
                      backgroundColor: 'color-mix(in srgb, var(--accent-primary) 15%, transparent)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 9, fontWeight: 800, color: 'var(--accent-primary)',
                    }}>{i + 1}</div>
                    <span style={{ fontSize: 11, color: 'var(--text-secondary)', lineHeight: 1.5 }}>{item}</span>
                  </div>
                ))}
              </div>

              {/* Expiry */}
              {payment.expires_at && (
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '10px 14px', borderRadius: 12,
                  backgroundColor: 'color-mix(in srgb, var(--accent-warning) 6%, transparent)',
                  border: '1px solid color-mix(in srgb, var(--accent-warning) 20%, transparent)',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Clock size={14} style={{ color: 'var(--accent-warning)' }} />
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--accent-warning)' }}>Expires in</span>
                  </div>
                  <Countdown expiresAt={payment.expires_at} />
                </div>
              )}

              {/* Waiting */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '12px 14px', borderRadius: 12, backgroundColor: 'var(--bg-secondary)',
              }}>
                <Loader2 size={20} style={{ color: 'var(--accent-primary)', animation: 'spin 1s linear infinite', flexShrink: 0 }} />
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)' }}>
                    Waiting for blockchain confirmation
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>
                    This page will update automatically once your payment is detected on-chain
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--text-tertiary)' }}>
                <Shield size={12} />
                <span>Verified directly on the blockchain — no intermediaries</span>
              </div>
            </>
          )}

          {/* ── STEP 3: Confirmed ── */}
          {step === 'confirmed' && (
            <div style={{ textAlign: 'center', padding: '16px 0', display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{
                width: 64, height: 64, borderRadius: '50%', margin: '0 auto',
                backgroundColor: 'color-mix(in srgb, var(--accent-success) 15%, transparent)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <CheckCircle2 size={32} style={{ color: 'var(--accent-success)' }} />
              </div>
              <div>
                <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--text-primary)' }}>
                  Payment Confirmed
                </div>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 6 }}>
                  {type === PAYMENT_TYPES.PLUGIN_PURCHASE && actualItem
                    ? <><strong>{actualItem.name || actualItem.label}</strong> has been activated</>
                    : type === PAYMENT_TYPES.DEPOSIT ? 'Your deposit has been credited'
                    : type === PAYMENT_TYPES.SUBSCRIPTION ? 'Your subscription is now active'
                    : 'Your payment has been confirmed'}
                </div>
              </div>
              {payment?.tx_hash && (
                <div style={{
                  padding: '8px 12px', borderRadius: 10, backgroundColor: 'var(--bg-secondary)',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
                }}>
                  <span style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--text-tertiary)' }}>
                    TX: {payment.tx_hash.slice(0, 20)}...
                  </span>
                  <CopyBtn text={payment.tx_hash} label="Copy TX" />
                </div>
              )}
              <button onClick={() => { onSuccess?.(); onClose(); }} style={{
                width: '100%', padding: '13px 0', borderRadius: 14,
                fontSize: 13, fontWeight: 700, color: 'white',
                backgroundColor: 'var(--accent-success)', border: 'none', cursor: 'pointer',
              }}>
                Done
              </button>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}